import * as React from 'react';
import { memo, useMemo, type JSX } from 'react';
import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react';
import { Variable } from '@restworld/utility-types';

import { HelperTextRElement, InputProps, TextInput } from '../..';
import { Heading, SkeletonLoading } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

type TVariable = Variable & {
  disabled?: boolean;
};
interface IRadioGroupProps extends Pick<InputProps, 'onChange' | 'borderless' | 'value' | 'id'> {
  inlineTextInputProps?: InputProps;
  testId?: string;
  mainTitle?: string | JSX.Element;
  subTitle?: string;
  options: TVariable[];
  returnKey?: string;
  showVerticalLineBelow?: boolean;
  twStyle?: string;
  helperText?: string;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  customOptionStyle?: string;
}

// type BgColor = 'white' | 'primary';
// type Spacing = 'small' | 'medium' | 'large';

export const RadioGroup = memo((props: IRadioGroupProps) => {
  const { mainTitle, helperText, subTitle, twStyle, orientation, ...rest } = props;

  return (
    <div className="flex gap-6 flex-col">
      {mainTitle && (
        <Heading size={4} weight="semibold" align="center">
          {mainTitle}
        </Heading>
      )}
      {subTitle && (
        <Heading size={6} weight="normal" color="muted" align="center">
          {subTitle}
        </Heading>
      )}
      <RadioGroupWrapper orientation={orientation} twStyle={twStyle}>
        <RadioComponent orientation={orientation} {...rest} />
        {helperText && <HelperTextRElement helperText={helperText} />}
      </RadioGroupWrapper>
    </div>
  );
});

const RadioGroupWrapper = ({ orientation, twStyle, className, ...props }: any) => (
  <div
    className={cn(
      'mx-auto w-full max-w-sm md:max-h-80 md:overflow-y-auto flex flex-col gap-2',
      orientation === 'horizontal' ? 'max-w-max' : '',
      twStyle,
      className
    )}
    {...props}
  />
);

const RadioComponent = memo((props: IRadioGroupProps) => {
  const { options, onChange, testId, orientation = 'vertical', ...rest } = props;

  return (
    <HeadlessRadioGroup
      className={orientation === 'horizontal' ? 'flex flex-row flex-wrap gap-3' : 'flex flex-col gap-3'}
      as="ul"
      data-cy={testId}
      onChange={onChange}
      {...rest}
    >
      {options ? (
        options.map((option, index) => (
          <Radio orientation={orientation} key={option.id + index} option={option} index={index} {...rest} />
        ))
      ) : (
        <SkeletonLoading variant="text" />
      )}
    </HeadlessRadioGroup>
  );
});

interface RadioOptionProps extends Omit<IRadioGroupProps, 'options'> {
  option: TVariable;
  index: number;
}

export const Radio = ({
  option,
  index,
  value,
  returnKey = 'id',
  borderless,
  showVerticalLineBelow,
  id,
  inlineTextInputProps,
  testId,
  orientation,
  customOptionStyle,
}: RadioOptionProps) => {
  const returnKeyWithType = returnKey as keyof typeof option;
  const optionValue = option[returnKeyWithType];
  const checked = useMemo(() => value === optionValue, [optionValue, value]);
  const returnId = useMemo(
    () => (returnKeyWithType && optionValue ? `${optionValue?.toString()}-${id}` : option.id),
    [id, option.id, optionValue, returnKeyWithType]
  );
  const hasInput = useMemo(() => value === 'other' && option.id === 'other', [option.id, value]);

  const otherOptionInput = useMemo(() => {
    if (inlineTextInputProps && hasInput) return <TextInput type="text" name="other" {...inlineTextInputProps} />;
    else return null;
  }, [hasInput, inlineTextInputProps]);

  return (
    <HeadlessRadioGroup.Option
      data-cy={testId || `${option.id}-${id}`}
      onKeyDown={(e) => {
        if (value === 'other') e.stopPropagation();
      }}
      key={option.id + index}
      value={optionValue}
      id={returnId}
      as="li"
      className="min-w-fit"
      disabled={option.disabled}
    >
      {({ active, disabled }) => (
        <StyledRadioOption
          id={`radiogroup-StyledOption-${option.label}`}
          showVerticalLineBelow={showVerticalLineBelow}
          borderless={borderless}
          active={active}
          checked={checked}
          hasInput={hasInput}
          orientation={orientation}
          disabled={disabled}
          customOptionStyle={customOptionStyle}
        >
          <SelectIcon checked={checked} />
          <div className="flex flex-col gap-2 w-full">
            <HeadlessRadioGroup.Label
              id={`radiogroup-label-${option.label}`}
              className="font-medium text-base cursor-pointer leading-4"
            >
              {option.label}
            </HeadlessRadioGroup.Label>
            {otherOptionInput}
          </div>
        </StyledRadioOption>
      )}
    </HeadlessRadioGroup.Option>
  );
};

const StyledRadioOption = ({
  borderless,
  showVerticalLineBelow,
  orientation,
  customOptionStyle,
  hasInput,
  active,
  checked,
  disabled,
  className,
  children,
  ...props
}: Pick<IRadioGroupProps, 'borderless' | 'showVerticalLineBelow' | 'orientation' | 'customOptionStyle'> & {
  hasInput?: boolean;
  active?: boolean;
  checked?: boolean;
  disabled?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'w-full',
      'border border-neutral-100 py-2.25 px-3 rounded-lg text-black flex gap-3 items-center justify-start max-w-[28rem] min-w-[12rem] cursor-pointer',
      checked && 'bg-purple-500 text-white border-purple-500',
      borderless && 'border-none',
      showVerticalLineBelow && 'border-gray-300 border-b px-0',
      hasInput && 'items-start',
      orientation === 'horizontal' && 'min-w-fit justify-center',
      disabled && 'bg-neutral-40 pointer-events-none border-gray-300 text-neutral-200',
      customOptionStyle,
      className
    )}
    {...props}
  >
    {children}
  </div>
);

const SelectIcon = ({ checked, className, ...props }: any) => (
  <span
    className={cn(
      'rounded-full border p-2 border-neutral-100',
      checked && 'border-white border-4 p-1',
      !checked && 'hover:ring-purple-50 hover:ring-8',
      className
    )}
    {...props}
  />
);
export default RadioGroup;
// ${({ bgColor = 'primary' }) => ({ white: 'bg-white', primary: 'bg-primary-50' }[bgColor])}
// ${({ spacing = 'medium' }) => ({ small: 'py-2 px-2 text-sm', medium: 'py-4 px-2 text-base', large: 'py-6 px-4 text-lg' }[spacing])}
